import Giveback2021Footer from '@/components/Giveback2021/Giveback2021Footer';
import Giveback2021KaleidoscopeContainer from '@/components/Giveback2021/Giveback2021KaleidoscopeContainer';
import Giveback2021SpecialButton from '@/components/Giveback2021/Giveback2021SpecialButton';
import Giveback2021Title from '@/components/Giveback2021/Giveback2021Title';
import {
  ACTUAL_HEIGHT,
  SCROLL_RATIO,
  SHARING_CONTENT,
  SHARING_EVENT,
  SHARING_URL,
  TITLE,
} from '@/components/Giveback2021/giveback2021Consts';
import { useGiveBack2021EasterEgg } from '@/components/Giveback2021/useGiveback2021EasterEgg';
import { useGiveback2021Presentation } from '@/components/Giveback2021/useGiveback2021Presentation';
import usePrefersReducedMotion from '@/components/Giveback2021/usePrefersReducedMotion';
import useRandomKey from '@/components/Giveback2021/useRandomKey';
import useValueAtRest from '@/components/Giveback2021/useValueAtRest';
import GivebackShareButton, { globalShare } from '@/components/GivebackShared/GivebackShareButton';
import Tweener from '@/components/GivebackShared/Tweener';
import Layout from '@/components/shared/Layout';
import { SEO } from '@/components/shared/SEO';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/giveback_2021/og.jpg';
import { fadeOutAnimation } from '@lemonade-hq/cdk';
import type { Milliseconds, Proportion } from '@lemonade-hq/ts-helpers';
import type { NextPage, NextPageContext } from 'next';
import dynamic from 'next/dynamic';
import type React from 'react';
import styled from 'styled-components';
import useSmoothScroll from '../hooks/useSmoothScroll';
import { GIVEBACK_LANDING_PAGE } from '../utils/productAdaPages';
import { useI18nContext } from '@lemonade-hq/nuiza';

const Giveback2021EasterEgg = dynamic(() => import('@/components/Giveback2021/Giveback2021EasterEgg'), { ssr: false });

interface PageProps {
  presentationMode: boolean;
  isFirefox: boolean;
  hideHeader: boolean;
}

const SHARE_BUTTON_T_START_THRESHOLD: Proportion = 0.03;
const SHARE_BUTTON_T_END_THRESHOLD: Proportion = 0.04;
const SHARE_BUTTON_REST_DURATION: Milliseconds = 250;

const ScrollContainer = styled.div`
  width: 100vw;
  height: ${ACTUAL_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (prefers-reduced-motion) {
    height: 40000px;
  }
`;

const ShareButton = styled(Giveback2021SpecialButton)`
  width: 106px;
  position: fixed;
  bottom: 20px;
  right: 20px;
`;

const Shutter = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  content: '';
  height: 100vh;
  width: 100vw;
  background-color: white;
  z-index: 1000;
  pointer-events: none;
  animation: ${fadeOutAnimation} 1000ms ease-out both;
`;

const Giveback2021Contents: React.FC<{ t: number } & PageProps> = ({ t, presentationMode, isFirefox }) => {
  const bottomShareButtonShown = useValueAtRest({
    changingValue: t,
    duration: SHARE_BUTTON_REST_DURATION,
    condition: t > SHARE_BUTTON_T_START_THRESHOLD && t < 1 - SHARE_BUTTON_T_END_THRESHOLD,
  });
  const { key: shutterKey, regenerateKey } = useRandomKey();

  const prefersReducedMotion = usePrefersReducedMotion();
  return (
    <>
      <ScrollContainer>
        <Giveback2021Title t={t} presentationMode={presentationMode} />
        <Tweener
          t={t}
          tt={
            prefersReducedMotion
              ? [
                  [0.01, 0.98],
                  [0.03, 0.98],
                  [0.03, 0.98],
                ]
              : [
                  [0.002 * SCROLL_RATIO, 0.998],
                  [0.0085 * SCROLL_RATIO, 0.995],
                  [0.01 * SCROLL_RATIO, 0.995],
                ]
          }
        >
          <Giveback2021KaleidoscopeContainer isFirefox={isFirefox} presentationMode={presentationMode} />
        </Tweener>
        <Giveback2021Footer t={t} presentationMode={presentationMode} />
      </ScrollContainer>
      {!presentationMode && t < 1 - SHARE_BUTTON_T_END_THRESHOLD && (
        <ShareButton
          onClick={() => {
            regenerateKey();
            globalShare(true, TITLE, SHARING_CONTENT, SHARING_URL);
          }}
          shown={bottomShareButtonShown}
        >
          Share
        </ShareButton>
      )}
      {shutterKey && <Shutter key={shutterKey.toString()} />}
    </>
  );
};

const Giveback2021Page: NextPage<{ url: { protocol: string; host: string } } & PageProps> = ({
  presentationMode,
  hideHeader,
  isFirefox,
}) => {
  const { smoothRelativeScroll: t } = useSmoothScroll({
    ssrHelp: true,
    strength: 100000,
  });
  const easterEggEnabled = useGiveBack2021EasterEgg(t);
  useGiveback2021Presentation(presentationMode);
  const { locale } = useI18nContext();

  const seoDescription =
    locale === 'nl-NL'
      ? 'Discover the results of Lemonade’s 2021 Giveback, supporting nonprofits with community-driven donations and positive change.'
      : locale === 'en-US'
        ? "65 nonprofits are receiving a donation via the 2021 Lemonade Giveback. Here's the impact you made."
        : '65 non-profitten ontvangen een donatie via de 2021 Lemonade Giveback. Hier is de impact die je hebt gemaakt.';

  return (
    <Layout
      {...sharedConfig['en-US'].layout}
      alignItems="center"
      overflow="hidden"
      pt={presentationMode ? [100, 190, 190] : [90, 150, 150]}
      pb={presentationMode ? ['7px', '7px', '7px'] : [20, 20, 20]}
      position="relative"
      CustomHeaderCTA={
        <GivebackShareButton
          nativeSharing
          event={SHARING_EVENT}
          url={SHARING_URL}
          title={TITLE}
          content={SHARING_CONTENT}
        />
      }
      showUserAccountComponent={false}
      //   showLogin={false}
      showBackToYourQuote={false}
      stickyHeader={false}
      fixedHeader
      showFooter={false}
      showHeader={!hideHeader || !easterEggEnabled || !presentationMode}
      page={GIVEBACK_LANDING_PAGE}
    >
      <SEO title={TITLE} metaTitle={TITLE} description={seoDescription} ogImage={ogImage.src} />
      {easterEggEnabled ? (
        <Giveback2021EasterEgg />
      ) : (
        <Giveback2021Contents hideHeader={hideHeader} presentationMode={presentationMode} isFirefox={isFirefox} t={t} />
      )}
    </Layout>
  );
};

export function getServerSideProps(ctx: NextPageContext): { props: PageProps } {
  const { query } = ctx;
  const presentationMode = !!query.present;
  const hideHeader = !!query.hide_header;
  const userAgent = ctx?.req?.headers['user-agent'] ?? '';

  return {
    props: {
      presentationMode, // presentation mode is calibrated for 960 x 540 resolution (TVs resolution).
      hideHeader,
      isFirefox: /firefox/i.test(userAgent),
    },
  };
}

export default Giveback2021Page;
